import { useEffect, useState } from "react";
import { Loader2, Copy, Check, Trash2, Plus, RefreshCw, DollarSign, Send, AlertCircle, ArrowRight, Server, BookUser } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";

const STATUS_CLS = {
    PROCESSING: "bg-amber-100 text-amber-800",
    SUCCESS: "bg-emerald-100 text-emerald-800",
    FAILED: "bg-rose-100 text-rose-800",
    REVERSED: "bg-slate-200 text-slate-700",
};

const EMPTY_SEND = {
    beneficiary_name: "", account_number: "", ifsc: "", bank_name: "",
    amount: "", payment_type: "AUTO", client_reference_id: "", mid: "",
    mfa_code: "",
};

const PAYOUT_MFA_THRESHOLD = 10000;

export default function PayoutsPage() {
    const [wallet, setWallet] = useState(null);
    const [keys, setKeys] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [ledger, setLedger] = useState([]);
    const [partnerMids, setPartnerMids] = useState([]);
    const [tab, setTab] = useState("send");
    const [err, setErr] = useState("");
    const [newKey, setNewKey] = useState(null);
    const [showKeyModal, setShowKeyModal] = useState(false);
    const [keyName, setKeyName] = useState("");
    const [copied, setCopied] = useState(false);
    const [whUrl, setWhUrl] = useState("");
    const [whFmt, setWhFmt] = useState("paygateway");
    const [whBusy, setWhBusy] = useState(false);
    const [sendForm, setSendForm] = useState(EMPTY_SEND);
    const [sendBusy, setSendBusy] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [sendErr, setSendErr] = useState(null);  // {kind, message, refunded}
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [showBook, setShowBook] = useState(false);
    const [bookQuery, setBookQuery] = useState("");

    const refresh = async () => {
        setErr("");
        try {
            const [w, k, p, l, m, b] = await Promise.all([
                api.get("/partner/payouts/wallet"),
                api.get("/partner/payouts/api-keys"),
                api.get("/partner/payouts"),
                api.get("/partner/payouts/ledger"),
                api.get("/partner/payouts/mids"),
                api.get("/partner/beneficiaries"),
            ]);
            setWallet(w.data); setKeys(k.data.items || []);
            setPayouts(p.data.items || []); setLedger(l.data.items || []);
            setPartnerMids(m.data.items || []);
            setBeneficiaries(b.data.items || []);
            setWhUrl(w.data.webhook_url || "");
            setWhFmt(w.data.webhook_format || "paygateway");
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        }
    };
    useEffect(() => { refresh(); }, []);

    const pickBeneficiary = (b) => {
        setSendForm({
            ...sendForm,
            beneficiary_name: b.name || "",
            account_number: b.account_number || "",
            ifsc: b.ifsc || "",
            bank_name: b.bank_name || "",
        });
        setShowBook(false);
    };

    const sendPayout = async () => {
        setErr(""); setSendErr(null); setSendBusy(true); setSendResult(null);
        try {
            const amt = Number(sendForm.amount);
            if (!amt || amt <= 0) { setErr("Enter a valid amount"); setSendBusy(false); return; }
            if (!sendForm.beneficiary_name.trim() || !sendForm.account_number.trim() || !sendForm.ifsc.trim()) {
                setErr("Beneficiary name, account number and IFSC are required");
                setSendBusy(false); return;
            }
            const body = {
                amount: amt,
                beneficiary: {
                    name: sendForm.beneficiary_name.trim(),
                    account_number: sendForm.account_number.trim(),
                    ifsc: sendForm.ifsc.trim().toUpperCase(),
                    bank_name: sendForm.bank_name.trim() || null,
                },
                payment_type: sendForm.payment_type || "AUTO",
            };
            if (sendForm.client_reference_id.trim()) body.client_reference_id = sendForm.client_reference_id.trim();
            if (sendForm.mid) body.mid = sendForm.mid;
            if (sendForm.mfa_code.trim()) body.mfa_code = sendForm.mfa_code.trim();
            const r = await api.post("/partner/payouts/send", body);
            setSendResult(r.data);
            setSendForm(EMPTY_SEND);
            refresh();
        } catch (e) {
            const d = e?.response?.data?.detail;
            // Vendor errors come back as dicts with {kind, message, refunded}
            // — surface those with a dedicated chip in the UI.
            if (d && typeof d === "object" && d.kind) {
                setSendErr(d);
                refresh();   // wallet got refunded; refresh balance
            } else {
                setErr(formatDetail(d) || e.message);
            }
        } finally { setSendBusy(false); }
    };

    const createKey = async () => {
        if (!keyName.trim()) return;
        try {
            const r = await api.post("/partner/payouts/api-keys", { name: keyName.trim() });
            setNewKey(r.data); setKeyName(""); setShowKeyModal(false);
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const revokeKey = async (id) => {
        if (!window.confirm("Revoke this API key permanently?")) return;
        try { await api.delete(`/partner/payouts/api-keys/${id}`); refresh(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const saveWebhook = async () => {
        setWhBusy(true); setErr("");
        try {
            await api.patch("/partner/payouts/webhook-config", {
                webhook_url: whUrl, webhook_format: whFmt,
            });
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setWhBusy(false); }
    };

    return (
        <div data-testid="payouts-page" className="space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Send className="w-6 h-6 text-blue-600" /> Payouts
                    </h1>
                    <p className="text-xs text-slate-500 mt-1">Send money to bank accounts via our REST API.</p>
                </div>
                <button onClick={refresh} className="btn-secondary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            {/* Wallet card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-blue-700 font-semibold">
                    <DollarSign className="w-3.5 h-3.5" /> Wallet balance
                </div>
                <div data-testid="payouts-balance" className="text-4xl font-bold text-blue-900 mt-1 font-mono">
                    {wallet ? fmtINR(wallet.balance) : "—"}
                </div>
                <div className="text-[11px] text-blue-700/70 mt-1">
                    Prepaid · Debited on each payout · Refunded on FAILED/REVERSED
                </div>
                {wallet && (
                    <div className="mt-3 pt-3 border-t border-blue-200/60">
                        <div className="text-[10px] uppercase tracking-wider text-blue-700/70 font-semibold mb-1">
                            Enabled vendors
                        </div>
                        {(wallet.allowed_vendors || []).length === 0 ? (
                            <div data-testid="payouts-vendors-empty" className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 inline-flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" /> No vendors enabled — contact support to activate payouts
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-1.5" data-testid="payouts-vendors-list">
                                {wallet.allowed_vendors.map((v) => (
                                    <span key={v} className="px-2 py-0.5 rounded-full bg-white border border-blue-200 text-blue-800 text-[11px] font-semibold">
                                        {v}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {err && (
                <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700 flex items-center gap-2">
                    <AlertCircle className="w-3.5 h-3.5" /> {err}
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-slate-200 flex gap-6 overflow-x-auto">
                {[
                    ["send", "Send Payout"],
                    ["payouts", `History (${payouts.length})`],
                    ["mids", `MIDs (${partnerMids.length})`],
                    ["keys", `API keys (${keys.length})`],
                    ["webhook", "Webhook"],
                    ["ledger", `Ledger (${ledger.length})`],
                ].map(([k, label]) => (
                    <button key={k} data-testid={`po-tab-${k}`} onClick={() => setTab(k)}
                        className={`py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
                            tab === k ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-500"
                        }`}>{label}</button>
                ))}
            </div>

            {tab === "send" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
                        <h3 className="font-bold text-base mb-1">Send Payout</h3>
                        <p className="text-xs text-slate-500 mb-5">Transfer money to any bank account in India.</p>

                        {partnerMids.length === 0 ? (
                            <div data-testid="send-no-mids" className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                                No MIDs assigned to your account. Contact support to activate payouts.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {beneficiaries.length > 0 && (
                                    <div className="flex items-center justify-between bg-indigo-50 border border-indigo-100 rounded-md p-2.5">
                                        <div className="text-xs text-indigo-900">
                                            <strong>{beneficiaries.length}</strong> saved beneficiar{beneficiaries.length === 1 ? "y" : "ies"} — pick one to pre-fill the form.
                                        </div>
                                        <button type="button" data-testid="send-pick-book" onClick={() => setShowBook(true)}
                                                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 flex items-center gap-1.5">
                                            <BookUser className="w-3.5 h-3.5" /> Pick from book
                                        </button>
                                    </div>
                                )}
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Beneficiary Name</label>
                                    <input data-testid="send-bene-name" value={sendForm.beneficiary_name}
                                            onChange={(e) => setSendForm({ ...sendForm, beneficiary_name: e.target.value })}
                                            placeholder="John Doe" className="input w-full text-sm mt-1" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Account Number</label>
                                        <input data-testid="send-account" value={sendForm.account_number}
                                                onChange={(e) => setSendForm({ ...sendForm, account_number: e.target.value })}
                                                placeholder="000123456789" className="input w-full text-sm mt-1 font-mono" />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">IFSC</label>
                                        <input data-testid="send-ifsc" value={sendForm.ifsc}
                                                onChange={(e) => setSendForm({ ...sendForm, ifsc: e.target.value.toUpperCase() })}
                                                placeholder="HDFC0001234" className="input w-full text-sm mt-1 font-mono uppercase" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Bank Name (optional)</label>
                                    <input data-testid="send-bank" value={sendForm.bank_name}
                                            onChange={(e) => setSendForm({ ...sendForm, bank_name: e.target.value })}
                                            placeholder="HDFC Bank" className="input w-full text-sm mt-1" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Amount (₹)</label>
                                        <input data-testid="send-amount" type="number" value={sendForm.amount}
                                                onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })}
                                                placeholder="1000" className="input w-full text-sm mt-1 font-mono text-lg" />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Client Reference (optional)</label>
                                        <input data-testid="send-ref" value={sendForm.client_reference_id}
                                                onChange={(e) => setSendForm({ ...sendForm, client_reference_id: e.target.value })}
                                                placeholder="order_12345" className="input w-full text-sm mt-1 font-mono" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">MID (default: first assigned)</label>
                                    <select data-testid="send-mid" value={sendForm.mid}
                                            onChange={(e) => setSendForm({ ...sendForm, mid: e.target.value })}
                                            className="input w-full text-sm mt-1">
                                        <option value="">★ Auto — use default MID</option>
                                        {partnerMids.map((m) => (
                                            <option key={m.mid_id} value={m.mid_id}
                                                    disabled={m.status !== "active"}
                                                    data-testid={`send-mid-opt-${m.mid_id}`}>
                                                {m.label} ({m.vendor}{m.status === "paused" ? " · paused" : ""})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Payment Type</label>
                                    <div className="grid grid-cols-4 gap-2 mt-1">
                                        {[
                                            ["AUTO", "Auto", "Gateway picks"],
                                            ["IMPS", "IMPS", "Max ₹5,00,000"],
                                            ["NEFT", "NEFT", "No upper limit"],
                                            ["RTGS", "RTGS", "Max ₹10,00,000"],
                                        ].map(([val, label, sub]) => (
                                            <button key={val} type="button"
                                                    data-testid={`send-pt-${val.toLowerCase()}`}
                                                    onClick={() => setSendForm({ ...sendForm, payment_type: val })}
                                                    className={`p-2.5 rounded-md text-xs border transition ${
                                                        sendForm.payment_type === val
                                                            ? "border-blue-500 bg-blue-50 text-blue-800 font-semibold"
                                                            : "border-slate-200 hover:border-slate-300"
                                                    }`}>
                                                <div className="font-semibold">{label}</div>
                                                <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <button onClick={sendPayout} disabled={sendBusy}
                                        data-testid="send-submit"
                                        className="w-full btn-primary py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2">
                                    {sendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    Send ₹{sendForm.amount || "0"} <ArrowRight className="w-4 h-4" />
                                </button>

                                {Number(sendForm.amount) > PAYOUT_MFA_THRESHOLD && (
                                    <div data-testid="send-mfa-wrap" className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                        <div className="flex items-center gap-2 text-xs text-amber-900 font-semibold mb-2">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                            Amount above ₹{PAYOUT_MFA_THRESHOLD.toLocaleString("en-IN")} — enter authenticator code
                                        </div>
                                        <input data-testid="send-mfa-code" type="text" inputMode="numeric" maxLength={6}
                                                value={sendForm.mfa_code}
                                                onChange={(e) => setSendForm({ ...sendForm, mfa_code: e.target.value.replace(/\D/g, "") })}
                                                placeholder="123 456"
                                                className="input w-full text-base mt-1 font-mono tracking-[0.3em] text-center" />
                                        <p className="text-[10px] text-amber-800/70 mt-1.5">Open your authenticator app (Google / Authy / 1Password) and enter the current 6-digit code.</p>
                                    </div>
                                )}

                                {sendErr && (
                                    <div data-testid={`send-vendor-err-${sendErr.kind}`}
                                        className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-200 text-rose-800">
                                                    Vendor rejected
                                                </span>
                                                {sendErr.refunded && (
                                                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                                                        ✓ Refunded
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-rose-900 mt-1.5 leading-relaxed">{sendErr.message}</p>
                                        </div>
                                    </div>
                                )}

                                {sendResult && (
                                    <div data-testid="send-result" className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs">
                                        <div className="font-semibold text-emerald-800 mb-1">Payout queued ✓</div>
                                        <div className="font-mono text-emerald-900">{sendResult.payout_id}</div>
                                        <div className="text-[10px] text-emerald-700 mt-1">
                                            Status: {sendResult.status} · {fmtINR(sendResult.amount)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="bg-white border border-slate-200 rounded-xl p-5">
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Available Balance</div>
                            <div data-testid="send-balance" className="text-2xl font-bold mt-1 font-mono">{wallet ? fmtINR(wallet.balance) : "—"}</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-5">
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Payment Summary</div>
                            <div className="flex justify-between text-xs py-1"><span className="text-slate-600">Amount to beneficiary</span><span className="font-mono">{fmtINR(Number(sendForm.amount) || 0)}</span></div>
                            <div className="flex justify-between text-xs py-1"><span className="text-slate-600">Fee</span><span className="font-mono">₹0.00</span></div>
                            <div className="flex justify-between text-xs py-1 border-t border-slate-100 mt-1 pt-2 font-semibold"><span>Remaining after payout</span><span className="font-mono">{wallet ? fmtINR((wallet.balance || 0) - (Number(sendForm.amount) || 0)) : "—"}</span></div>
                        </div>
                    </div>
                </div>
            )}

            {tab === "mids" && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                            <tr>
                                <th className="px-3 py-2 text-left">Priority</th>
                                <th className="px-3 py-2 text-left">Label</th>
                                <th className="px-3 py-2 text-left">Vendor</th>
                                <th className="px-3 py-2 text-right">Daily / Min / Max</th>
                                <th className="px-3 py-2 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {partnerMids.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No MIDs assigned. Contact support.</td></tr>}
                            {partnerMids.map((m, i) => (
                                <tr key={m.mid_id} data-testid={`partner-mid-${m.mid_id}`} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-center">
                                        {i === 0 && <span className="text-indigo-600 font-bold">★ Default</span>}
                                        {i > 0 && <span className="text-slate-400">#{i + 1}</span>}
                                    </td>
                                    <td className="px-3 py-2 font-semibold">{m.label}</td>
                                    <td className="px-3 py-2 text-slate-600">{m.vendor}</td>
                                    <td className="px-3 py-2 text-right text-[11px]">
                                        {m.daily_limit ? `₹${m.daily_limit}/day` : "—"}<br/>
                                        <span className="text-slate-400">{m.min_payout || 0} – {m.max_payout || "∞"}</span>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                            m.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                                        }`}>
                                            {m.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "payouts" && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                            <tr>
                                <th className="px-3 py-2 text-left">Payout ID</th>
                                <th className="px-3 py-2 text-left">Ref</th>
                                <th className="px-3 py-2 text-left">Beneficiary</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-left">UTR</th>
                                <th className="px-3 py-2 text-left">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payouts.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No payouts yet. Fire your first one via the API.</td></tr>}
                            {payouts.map((p) => (
                                <tr key={p.payout_id} data-testid={`po-row-${p.payout_id}`} className="border-t border-slate-100">
                                    <td className="px-3 py-2 font-mono text-slate-700">{p.payout_id}</td>
                                    <td className="px-3 py-2 font-mono text-slate-500">{p.client_reference_id || "—"}</td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium">{p.beneficiary?.name}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">{p.beneficiary?.account_number} · {p.beneficiary?.ifsc}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtINR(p.amount)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_CLS[p.status] || "bg-slate-100 text-slate-700"}`}>
                                            {p.status}
                                        </span>
                                        {p.failure_reason && <div className="text-[10px] text-rose-600 mt-0.5">{p.failure_reason}</div>}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{p.utr || "—"}</td>
                                    <td className="px-3 py-2 text-slate-500 text-[11px]">{new Date(p.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "keys" && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500">Use these keys to call <span className="font-mono">POST /api/v1/payout</span> with <span className="font-mono">X-API-Key</span>.</p>
                        <button onClick={() => setShowKeyModal(true)} data-testid="po-create-key"
                                className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                            <Plus className="w-3.5 h-3.5" /> New API key
                        </button>
                    </div>
                    <div className="border border-slate-200 rounded-lg">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
                                <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Key</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Last used</th><th className="px-3 py-2"></th></tr>
                            </thead>
                            <tbody>
                                {keys.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No keys yet.</td></tr>}
                                {keys.map((k) => (
                                    <tr key={k.api_key_id} className="border-t border-slate-100">
                                        <td className="px-3 py-2 font-medium">{k.name}</td>
                                        <td className="px-3 py-2 font-mono text-slate-500">{k.key_display}</td>
                                        <td className="px-3 py-2">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${k.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{k.status}</span>
                                        </td>
                                        <td className="px-3 py-2 text-slate-500 text-[11px]">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}</td>
                                        <td className="px-3 py-2 text-right">
                                            {k.status === "active" && (
                                                <button data-testid={`po-revoke-${k.api_key_id}`} onClick={() => revokeKey(k.api_key_id)} className="text-rose-600 hover:text-rose-800 text-xs"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "webhook" && (
                <div className="space-y-3 max-w-2xl">
                    <div>
                        <label className="text-xs font-medium text-slate-700">Webhook URL</label>
                        <input data-testid="po-wh-url" type="url" value={whUrl} onChange={(e) => setWhUrl(e.target.value)}
                                placeholder="https://yourapp.com/webhooks/payout" className="input w-full" />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-slate-700">Payload format</label>
                        <select data-testid="po-wh-fmt" value={whFmt} onChange={(e) => setWhFmt(e.target.value)} className="input w-full">
                            <option value="paygateway">PayGateway native (recommended)</option>
                            <option value="firstpay">FirstPay-compatible (drop-in migration)</option>
                        </select>
                        <p className="text-[11px] text-slate-500 mt-1">
                            {whFmt === "firstpay" ? "Pass-through FirstPay payload shape — zero code change for merchants migrating from FirstPay." : "Our schema: payout_id, client_reference_id, iso timestamps, full beneficiary."}
                        </p>
                    </div>
                    <button data-testid="po-wh-save" onClick={saveWebhook} disabled={whBusy}
                            className="btn-primary px-4 py-2 rounded-md text-xs flex items-center gap-2">
                        {whBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save webhook
                    </button>
                </div>
            )}

            {tab === "ledger" && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                            <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Dir</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">Reason</th><th className="px-3 py-2 text-left">Ref</th></tr>
                        </thead>
                        <tbody>
                            {ledger.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No ledger entries yet.</td></tr>}
                            {ledger.map((e) => (
                                <tr key={e.entry_id} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-slate-500 text-[11px]">{new Date(e.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${e.direction === "credit" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{e.direction}</span>
                                    </td>
                                    <td className={`px-3 py-2 text-right font-mono font-semibold ${e.direction === "credit" ? "text-emerald-700" : "text-rose-700"}`}>
                                        {e.direction === "credit" ? "+" : "−"}{fmtINR(e.amount)}
                                    </td>
                                    <td className="px-3 py-2 text-slate-700">{e.reason}</td>
                                    <td className="px-3 py-2 font-mono text-slate-500">{e.ref_id}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create-key modal */}
            {showKeyModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-96 shadow-xl">
                        <h3 className="font-bold text-lg mb-3">New API key</h3>
                        <input data-testid="po-key-name" autoFocus type="text" value={keyName} onChange={(e) => setKeyName(e.target.value)}
                                placeholder="e.g. Production server" className="input w-full mb-4" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowKeyModal(false)} className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button data-testid="po-key-submit" onClick={createKey} className="btn-primary px-3 py-2 rounded-md text-xs">Generate</button>
                        </div>
                    </div>
                </div>
            )}

            {/* New-key reveal modal */}
            {newKey && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-[520px] shadow-xl">
                        <h3 className="font-bold text-lg mb-1">Your new API key</h3>
                        <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mb-3">
                            Save this now — it won't be shown again.
                        </p>
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-xs break-all">
                            <span data-testid="po-new-key" className="flex-1">{newKey.key}</span>
                            <button onClick={() => { navigator.clipboard.writeText(newKey.key); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                                    className="text-blue-600 hover:text-blue-800">
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </button>
                        </div>
                        <div className="flex justify-end mt-4">
                            <button data-testid="po-new-key-close" onClick={() => setNewKey(null)} className="btn-primary px-4 py-2 rounded-md text-xs">I've saved it</button>
                        </div>
                    </div>
                </div>
            )}

            {showBook && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
                    <div data-testid="book-picker" className="bg-white rounded-xl w-full max-w-lg p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-base flex items-center gap-2"><BookUser className="w-4 h-4" /> Pick a beneficiary</h3>
                            <button onClick={() => setShowBook(false)} className="text-slate-400 hover:text-slate-600">✕</button>
                        </div>
                        <input autoFocus data-testid="book-search" value={bookQuery}
                                onChange={(e) => setBookQuery(e.target.value)}
                                placeholder="Search name, nickname, account, bank…"
                                className="input w-full text-sm" />
                        <div className="max-h-[400px] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                            {beneficiaries.filter((b) => {
                                if (!bookQuery.trim()) return true;
                                const q = bookQuery.trim().toLowerCase();
                                return [b.name, b.nickname, b.account_number, b.bank_name, b.upi_handle, ...(b.tags || [])]
                                    .some((v) => (v || "").toLowerCase().includes(q));
                            }).map((b) => (
                                <button key={b.beneficiary_id} data-testid={`book-pick-${b.beneficiary_id}`}
                                        onClick={() => pickBeneficiary(b)}
                                        className="w-full p-3 text-left hover:bg-indigo-50">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-sm">{b.name}</span>
                                        {b.nickname && <span className="text-xs text-slate-500">({b.nickname})</span>}
                                        {(b.tags || []).map((t) => (
                                            <span key={t} className="px-1.5 py-0.5 rounded-full text-[10px] bg-indigo-100 text-indigo-700 font-medium">{t}</span>
                                        ))}
                                    </div>
                                    <div className="text-xs text-slate-500 font-mono mt-0.5">
                                        {b.account_number ? `${b.account_number} · ${b.ifsc}` : b.upi_handle || "—"}
                                    </div>
                                </button>
                            ))}
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center justify-between">
                            <span>Can't find it? <a href="/partner/panel/beneficiaries" className="text-indigo-600 hover:underline">Open address book →</a></span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
